import { useState, type ReactNode } from 'react';
import { IconChevronLeft, IconChevronRight } from '../icons';
import { fadeMask, useOverflowEdges, useSidewaysWheel } from './overflowEdges';

/**
 * A horizontal strip that scrolls sideways and SAYS so: every tab strip in the app and the
 * crew map panel's filter draw through it (UX baseline).
 *
 * ⭐ Each end with more past it fades out (`fadeMask`) and grows a chevron that scrolls a page
 * that way, for a POINTER; a vertical wheel over the strip scrolls it sideways
 * (`useSidewaysWheel`). Nothing shows when everything fits.
 *
 * ⚠️ **The chevrons are not in the tab order** (`tabIndex={-1}`, `aria-hidden`), on purpose:
 * a keyboard already reaches every item (arrow keys on a tablist, Tab on buttons), and a stop
 * on a chevron sat between a page's active tab and its panel on every tab strip in the app
 * (Codex, ticket 04 review r2). Instead an item that takes the focus is scrolled FULLY into
 * view (`inline: 'nearest'`; a browser's own focus scroll may leave it half under the fade),
 * and the scroll updates the edges, so the fade and chevrons follow the keyboard too.
 *
 * ⚠️ The crew map's 416 px panel hid its "Done" tab behind an invisible overlay scrollbar
 * (macOS and headless Chromium draw none until you already scroll), and a strip cut off at a
 * whole item reads as complete. The hairline (`bordered`) sits on the wrapper, so the fade
 * never eats it.
 */
export function ScrollStrip({
  bordered,
  children,
}: {
  bordered?: boolean;
  children: ReactNode;
}) {
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const edges = useOverflowEdges(scroller, children);
  useSidewaysWheel(scroller);
  const page = (dir: 1 | -1) => {
    if (!scroller) return;
    const left = dir * Math.max(80, Math.round(scroller.clientWidth * 0.7));
    if (typeof scroller.scrollBy === 'function') {
      scroller.scrollBy({ left, behavior: 'smooth' });
    } else scroller.scrollLeft += left;
  };
  const mask = fadeMask(edges);
  return (
    <div
      data-strip-overflow={edges.start || edges.end ? 'true' : undefined}
      className={bordered ? 'relative border-b border-border' : 'relative'}
    >
      <div
        ref={setScroller}
        style={{ maskImage: mask, WebkitMaskImage: mask }}
        className="overflow-x-auto"
        onFocus={(e) => {
          const item = e.target as HTMLElement;
          if (item !== e.currentTarget && typeof item.scrollIntoView === 'function') {
            item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          }
        }}
      >
        {children}
      </div>
      {edges.start ? <ScrollCue side="start" onPress={() => page(-1)} /> : null}
      {edges.end ? <ScrollCue side="end" onPress={() => page(1)} /> : null}
    </div>
  );
}

/**
 * The chevron at an edge with more past it: a pointer's control only (see `ScrollStrip`).
 * Its mouse-down keeps the focus where it was, so a click never parks it on a hidden control.
 */
function ScrollCue({ side, onPress }: { side: 'start' | 'end'; onPress: () => void }) {
  const Icon = side === 'start' ? IconChevronLeft : IconChevronRight;
  return (
    <button
      type="button"
      data-strip-cue={side}
      tabIndex={-1}
      aria-hidden="true"
      title={side === 'start' ? 'Scroll left' : 'Scroll right'}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onPress}
      className={`absolute top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-full border border-border bg-surface text-muted-foreground shadow-sm hover:text-text ${
        side === 'start' ? 'left-0' : 'right-0'
      }`}
    >
      <Icon size={16} aria-hidden="true" />
    </button>
  );
}
